import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user]);

  useEffect(() => {
    filterSessions();
  }, [sessions, searchQuery]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterSessions = () => {
    let filtered = sessions;

    if (searchQuery) {
      filtered = filtered.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredSessions(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Mentality
                </h1>
                <p className="text-xs text-gray-500">The College Mentality</p>
              </div>
            </Link>
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">Dashboard</Button>
              </Link>
              <Link to="/sessions">
                <Button variant="ghost" size="sm" className="bg-mentality-100 text-mentality-700">Sessions</Button>
              </Link>
              <Link to="/mood">
                <Button variant="ghost" size="sm">Mood</Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout().then(() => navigate('/'))}
                className="border-mentality-200 hover:bg-mentality-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            The College Mentality Sessions 🎓
          </h2>
          <p className="text-gray-600">
            Guided wellness sessions designed specifically for college students
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <Input
            placeholder="Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md border-mentality-200 focus:border-mentality-400 focus:ring-mentality-300"
          />
        </div>

        {/* Sessions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <Link to={`/sessions/${session.id}`} key={session.id}>
              <Card className="overflow-hidden hover:shadow-xl transition-all border-2 hover:border-mentality-300 cursor-pointer h-full">
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={session.image_url}
                    alt={session.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-white/90 text-gray-700 backdrop-blur-sm">
                      {session.duration} min
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-mentality-100 text-mentality-700">
                      🎓 The College Mentality
                    </Badge>
                    <Badge variant="outline" className="text-xs border-mentality-300 text-mentality-600">
                      {session.difficulty}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-xl mb-2 text-gray-900">{session.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{session.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {filteredSessions.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No sessions found matching your search</p>
            <Button
              onClick={() => setSearchQuery('')}
              className="mt-4 bg-mentality-300 hover:bg-mentality-400 text-white"
              variant="outline"
            >
              Clear Search
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SessionsPage;
